// @file Content picker helper.
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { p__, __ } from '@@/bits/intl'
import { supportsRecording, supportsScreenCapture, supportsVideoRecording } from '@@/bits/media_support'
import { MAX_PHOTO_ALBUM_ATTACHMENTS } from '@@/bits/post_drafts'
import { wallAdminRoleLabel } from '@@/bits/wall_admin_role_label'
import { SearchFilters } from '@@/bits/web_search'
import { AttachmentTypeId, AttachmentTypeMode } from '@@/types'
import { groupBy } from 'es-toolkit'

/**
 * Whether the AV screen recorder should be offered. Requires browser support for
 * both screen capture (`getDisplayMedia`) and video recording (MediaRecorder with
 * a usable video container), and is a desktop-only affordance so it is hidden on
 * mobile UAs and tablet/phone form-factor variants.
 */
const isAVScreenRecorderSupported =
  supportsScreenCapture && supportsVideoRecording && !device.mobile && !device.tablet && !device.phone

interface ContentSource {
  type: ContentPickerSource
  text: string
  ariaLabel: string
  disabledAriaLabel?: string
  icon: string
  activeIcon: string
  color: 'purple' | 'blue' | 'green' | 'orange'
  visible: boolean
}

interface ContentPickerSourceMenuListItem {
  type: string
  text: string
  ariaLabel?: string
  icon?: string
  activeIcon?: string
  color: string
  hiddenInputId?: string
  visible: boolean
  disabled: boolean
  isNew?: boolean
  actionType?: string
  actionPayload?: string | object
  avatarUrl?: string
  avatarAltText?: string
  testId?: string
}

enum ContentPickerSource {
  FileBrowser = 'file',
  LinkInput = 'link',
  WebBrowser = 'web_search',
  PhotoBooth = 'photo_booth',
  VideoRecorder = 'video_recorder',
  AudioRecorder = 'audio_recorder',
  DrawingCanvas = 'drawing',
  PadletBrowser = 'padlet',
  MapBrowser = 'location_search',
  ScreenshareRecorder = 'screen_recorder',
  AiImageSearch = 'ai_image_search',
  ImageSearch = 'image_search',
  ThreeDObjectSearch = 'three_d_object_search',
  StockImageSearch = 'stock_image_search',
  VideoSearch = 'video_search',
  AudioSearch = 'audio_search',
  GifSearch = 'gif_search',
  Poll = 'poll',
  GoogleDrive = 'google_drive',
  MicrosoftOneDrive = 'microsoft_one_drive_picker',
  AiTalk = 'ai_talk',
  MediaLibrary = 'media_library',
  DocumentScanner = 'document_scanner',
  PhotoAlbum = 'photo_album',
  Stickers = 'stickers',
  DeviceUploads = 'device_uploads',
  ArcadeBrowser = 'arcade',
}

type NoSearchFilterError = 'NO_SEARCH_FILTER'

const LOADING_IMAGES_BG_COLOR = ['red', 'blue', 'green', 'yellow', 'purple', 'pink', 'cyan', 'orange']

// Image picker types that are allowed in photo album content picker
const ALLOWED_IMAGE_PICKER_SOURCES: ContentPickerSource[] = [
  ContentPickerSource.FileBrowser, // Upload (Desktop)
  ContentPickerSource.MediaLibrary, // Upload (Native App)
  ContentPickerSource.LinkInput, // Link
  ContentPickerSource.PhotoBooth, // Camera
  ContentPickerSource.DrawingCanvas, // Draw
  ContentPickerSource.AiImageSearch, // AI Image
  ContentPickerSource.ImageSearch, // Image Search
  ContentPickerSource.GifSearch, // GIF
]

/**
 * Checks if all image picker types are disabled
 * @param attachmentSettings Object containing attachment settings for each content picker source
 * @returns true if all image picker types are disabled, false otherwise
 */
const areAllImagePickerSourcesDisabledFromSettings = (
  attachmentSettings: Record<ContentPickerSource, boolean> | boolean,
): boolean => {
  if (typeof attachmentSettings === 'boolean') {
    return !attachmentSettings
  }
  return ALLOWED_IMAGE_PICKER_SOURCES.every((source) => !attachmentSettings[source])
}

const ATTACHMENT_TYPE_TO_CONTENT_PICKER_SOURCE_MAP: Partial<Record<AttachmentTypeId, ContentPickerSource>> = {
  [AttachmentTypeId.Upload]: ContentPickerSource.FileBrowser,
  [AttachmentTypeId.Link]: ContentPickerSource.LinkInput,
  [AttachmentTypeId.Camera]: ContentPickerSource.PhotoBooth,
  [AttachmentTypeId.Draw]: ContentPickerSource.DrawingCanvas,
  [AttachmentTypeId.ICantDraw]: ContentPickerSource.AiImageSearch,
  [AttachmentTypeId.ImageSearch]: ContentPickerSource.ImageSearch,
  [AttachmentTypeId.Gif]: ContentPickerSource.GifSearch,
}

/**
 * Checks if all image picker types are disabled based on attachment type states
 * @param attachmentTypeStates Object containing attachment type states
 * @returns true if all image picker types are disabled, false otherwise
 */
const areAllImagePickerSourcesDisabledFromAttachmentTypeStates = (
  attachmentTypeStates: Record<AttachmentTypeId, AttachmentTypeMode>,
): boolean => {
  // Get image picker attachment type IDs that map to ALLOWED_IMAGE_PICKER_SOURCES
  const imagePickerAttachmentTypeIds = Object.keys(ATTACHMENT_TYPE_TO_CONTENT_PICKER_SOURCE_MAP) as AttachmentTypeId[]

  // Check if all image picker attachment types are disabled
  return imagePickerAttachmentTypeIds.every((attachmentTypeId) => {
    return attachmentTypeStates[attachmentTypeId] === AttachmentTypeMode.Disabled
  })
}

const DEVICE_UPLOADABLE_ATTACHMENT_TYPE_IDS: AttachmentTypeId[] = [
  AttachmentTypeId.Upload,
  AttachmentTypeId.Camera,
  AttachmentTypeId.VideoRecorder,
  AttachmentTypeId.AudioRecorder,
  AttachmentTypeId.Draw,
  AttachmentTypeId.PhotoAlbum,
]

const areAllDeviceUploadableSourcesDisabledFromAttachmentTypeStates = (
  attachmentTypeStates: Record<AttachmentTypeId, AttachmentTypeMode>,
): boolean => {
  return DEVICE_UPLOADABLE_ATTACHMENT_TYPE_IDS.every((attachmentTypeId) => {
    return attachmentTypeStates[attachmentTypeId] === AttachmentTypeMode.Disabled
  })
}

// Some content sources like `MediaLibrary` is a mobile-app only content source.
// Technically, we'll never have this content source in the web code but since we set a strict type
// for this constant, we still need to implement it. It's an okay tradeoff to ensure the completeness
// of `CONTENT_PICKER_SOURCE_ARIA_LABEL_MAP_FOR_SETTINGS`.
type SettingsLabelContext = 'board' | 'whiteboard'

function getContentPickerSourceAriaLabelsForSettings(
  context: SettingsLabelContext,
): Record<ContentPickerSource, { enable: string; disable: string }> {
  if (context === 'whiteboard') {
    return {
      [ContentPickerSource.PadletBrowser]: {
        enable: __('Enable padlet attachment for this sandbox'),
        disable: __('Disable padlet attachment for this sandbox'),
      },
      [ContentPickerSource.FileBrowser]: {
        enable: __('Enable upload attachment for this sandbox'),
        disable: __('Disable upload attachment for this sandbox'),
      },
      [ContentPickerSource.LinkInput]: {
        enable: __('Enable link attachment for this sandbox'),
        disable: __('Disable link attachment for this sandbox'),
      },
      [ContentPickerSource.PhotoBooth]: {
        enable: __('Enable photo attachment for this sandbox'),
        disable: __('Disable photo attachment for this sandbox'),
      },
      [ContentPickerSource.VideoRecorder]: {
        enable: __('Enable video recording attachment for this sandbox'),
        disable: __('Disable video recording attachment for this sandbox'),
      },
      [ContentPickerSource.AudioRecorder]: {
        enable: __('Enable audio recording attachment for this sandbox'),
        disable: __('Disable audio recording attachment for this sandbox'),
      },
      [ContentPickerSource.ScreenshareRecorder]: {
        enable: __('Enable screen recording attachment for this sandbox'),
        disable: __('Disable screen recording attachment for this sandbox'),
      },
      [ContentPickerSource.DrawingCanvas]: {
        enable: __('Enable drawing attachment for this sandbox'),
        disable: __('Disable drawing attachment for this sandbox'),
      },
      [ContentPickerSource.Poll]: {
        enable: __('Enable poll attachment for this sandbox'),
        disable: __('Disable poll attachment for this sandbox'),
      },
      [ContentPickerSource.GoogleDrive]: {
        enable: __('Enable Google Drive attachment for this sandbox'),
        disable: __('Disable Google Drive attachment for this sandbox'),
      },
      [ContentPickerSource.MicrosoftOneDrive]: {
        enable: __('Enable Microsoft OneDrive attachment for this sandbox'),
        disable: __('Disable Microsoft OneDrive attachment for this sandbox'),
      },
      [ContentPickerSource.AiImageSearch]: {
        enable: __('Enable AI Art Generator attachment for this sandbox'),
        disable: __('Disable AI Art Generator attachment for this sandbox'),
      },
      [ContentPickerSource.AiTalk]: {
        enable: __('Enable AI Speech Generator attachment for this sandbox'),
        disable: __('Disable AI Speech Generator attachment for this sandbox'),
      },
      [ContentPickerSource.ImageSearch]: {
        enable: __('Enable image search for this sandbox'),
        disable: __('Disable image search for this sandbox'),
      },
      [ContentPickerSource.ThreeDObjectSearch]: {
        enable: __('Enable 3D model attachment for this sandbox'),
        disable: __('Disable 3D model attachment for this sandbox'),
      },
      [ContentPickerSource.StockImageSearch]: {
        enable: __('Enable stock image search for this sandbox'),
        disable: __('Disable stock image search for this sandbox'),
      },
      [ContentPickerSource.GifSearch]: {
        enable: __('Enable GIF attachment for this sandbox'),
        disable: __('Disable GIF attachment for this sandbox'),
      },
      [ContentPickerSource.VideoSearch]: {
        enable: __('Enable video search for this sandbox'),
        disable: __('Disable video search for this sandbox'),
      },
      [ContentPickerSource.AudioSearch]: {
        enable: __('Enable audio search for this sandbox'),
        disable: __('Disable audio search for this sandbox'),
      },
      [ContentPickerSource.WebBrowser]: {
        enable: __('Enable web search for this sandbox'),
        disable: __('Disable web search for this sandbox'),
      },
      [ContentPickerSource.MapBrowser]: {
        enable: __('Enable location attachment for this sandbox'),
        disable: __('Disable location attachment for this sandbox'),
      },
      [ContentPickerSource.MediaLibrary]: {
        enable: __('Enable photo and video library for this sandbox'),
        disable: __('Disable photo and video library for this sandbox'),
      },
      [ContentPickerSource.DocumentScanner]: {
        enable: __('Enable document scanner for this sandbox'),
        disable: __('Disable document scanner for this sandbox'),
      },
      [ContentPickerSource.PhotoAlbum]: {
        enable: __('Enable photo album attachment for this sandbox'),
        disable: __('Disable photo album attachment for this sandbox'),
      },
      [ContentPickerSource.Stickers]: {
        enable: __('Enable stickers for this sandbox'),
        disable: __('Disable stickers for this sandbox'),
      },
      [ContentPickerSource.DeviceUploads]: {
        enable: __('Enable upload from mobile for this sandbox'),
        disable: __('Disable upload from mobile for this sandbox'),
      },
      [ContentPickerSource.ArcadeBrowser]: {
        enable: __('Enable Arcade game attachment for this sandbox'),
        disable: __('Disable Arcade game attachment for this sandbox'),
      },
    }
  }
  return {
    [ContentPickerSource.PadletBrowser]: {
      enable: __('Enable padlet attachment for this board'),
      disable: __('Disable padlet attachment for this board'),
    },
    [ContentPickerSource.FileBrowser]: {
      enable: __('Enable upload attachment for this board'),
      disable: __('Disable upload attachment for this board'),
    },
    [ContentPickerSource.LinkInput]: {
      enable: __('Enable link attachment for this board'),
      disable: __('Disable link attachment for this board'),
    },
    [ContentPickerSource.PhotoBooth]: {
      enable: __('Enable photo attachment for this board'),
      disable: __('Disable photo attachment for this board'),
    },
    [ContentPickerSource.VideoRecorder]: {
      enable: __('Enable video recording attachment for this board'),
      disable: __('Disable video recording attachment for this board'),
    },
    [ContentPickerSource.AudioRecorder]: {
      enable: __('Enable audio recording attachment for this board'),
      disable: __('Disable audio recording attachment for this board'),
    },
    [ContentPickerSource.ScreenshareRecorder]: {
      enable: __('Enable screen recording attachment for this board'),
      disable: __('Disable screen recording attachment for this board'),
    },
    [ContentPickerSource.DrawingCanvas]: {
      enable: __('Enable drawing attachment for this board'),
      disable: __('Disable drawing attachment for this board'),
    },
    [ContentPickerSource.Poll]: {
      enable: __('Enable poll attachment for this board'),
      disable: __('Disable poll attachment for this board'),
    },
    [ContentPickerSource.GoogleDrive]: {
      enable: __('Enable Google Drive attachment for this board'),
      disable: __('Disable Google Drive attachment for this board'),
    },
    [ContentPickerSource.MicrosoftOneDrive]: {
      enable: __('Enable Microsoft OneDrive attachment for this board'),
      disable: __('Disable Microsoft OneDrive attachment for this board'),
    },
    [ContentPickerSource.AiImageSearch]: {
      enable: __('Enable AI Art Generator attachment for this board'),
      disable: __('Disable AI Art Generator attachment for this board'),
    },
    [ContentPickerSource.AiTalk]: {
      enable: __('Enable AI Speech Generator attachment for this board'),
      disable: __('Disable AI Speech Generator attachment for this board'),
    },
    [ContentPickerSource.ImageSearch]: {
      enable: __('Enable image search for this board'),
      disable: __('Disable image search for this board'),
    },
    [ContentPickerSource.ThreeDObjectSearch]: {
      enable: __('Enable 3D model attachment for this board'),
      disable: __('Disable 3D model attachment for this board'),
    },
    [ContentPickerSource.StockImageSearch]: {
      enable: __('Enable stock image search for this board'),
      disable: __('Disable stock image search for this board'),
    },
    [ContentPickerSource.GifSearch]: {
      enable: __('Enable GIF attachment for this board'),
      disable: __('Disable GIF attachment for this board'),
    },
    [ContentPickerSource.VideoSearch]: {
      enable: __('Enable video search for this board'),
      disable: __('Disable video search for this board'),
    },
    [ContentPickerSource.AudioSearch]: {
      enable: __('Enable audio search for this board'),
      disable: __('Disable audio search for this board'),
    },
    [ContentPickerSource.WebBrowser]: {
      enable: __('Enable web search for this board'),
      disable: __('Disable web search for this board'),
    },
    [ContentPickerSource.MapBrowser]: {
      enable: __('Enable location attachment for this board'),
      disable: __('Disable location attachment for this board'),
    },
    [ContentPickerSource.MediaLibrary]: {
      enable: __('Enable photo and video library for this board'),
      disable: __('Disable photo and video library for this board'),
    },
    [ContentPickerSource.DocumentScanner]: {
      enable: __('Enable document scanner for this board'),
      disable: __('Disable document scanner for this board'),
    },
    [ContentPickerSource.PhotoAlbum]: {
      enable: __('Enable photo album attachment for this board'),
      disable: __('Disable photo album attachment for this board'),
    },
    [ContentPickerSource.Stickers]: {
      enable: __('Enable stickers for this board'),
      disable: __('Disable stickers for this board'),
    },
    [ContentPickerSource.DeviceUploads]: {
      enable: __('Enable upload from mobile for this board'),
      disable: __('Disable upload from mobile for this board'),
    },
    [ContentPickerSource.ArcadeBrowser]: {
      enable: __('Enable Arcade game attachment for this board'),
      disable: __('Disable Arcade game attachment for this board'),
    },
  }
}

// If you update this, do update the schemas in services/rails/app/services/libraries/attachment_type_settings.rb too.
const getContentSources = (context: 'post' | 'whiteboard'): ContentSource[] =>
  [
    {
      type: ContentPickerSource.PadletBrowser,
      text: __('Padlet'),
      ariaLabel: __('Attach another padlet'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to attach another padlet'),
        __('A creator has disabled the option to attach another padlet'),
      ),
      icon: 'padlet_crane',
      activeIcon: 'padlet',
      color: 'purple',
      visible: true,
    },
    {
      type: ContentPickerSource.FileBrowser,
      text: __('Upload'),
      ariaLabel: __('Upload a file'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to upload files'),
        __('A creator has disabled the option to upload files'),
      ),
      icon: 'upload_outline',
      activeIcon: 'upload',
      color: 'purple',
      visible: true,
    },
    {
      type: ContentPickerSource.LinkInput,
      text: __('Link'),
      ariaLabel: __('Attach a link'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to attach links'),
        __('A creator has disabled the option to attach links'),
      ),
      icon: 'link',
      activeIcon: 'link_filled',
      color: 'purple',
      visible: true,
    },
    {
      type: ContentPickerSource.ArcadeBrowser,
      text: p__('Brand name, do not translate', 'Padlet Arcade'),
      ariaLabel: __('Attach an Arcade game'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to attach Arcade games'),
        __('A creator has disabled the option to attach Arcade games'),
      ),
      icon: 'arcade_outline',
      activeIcon: 'arcade_outline',
      color: 'purple',
      visible: isAppUsing('arcadeAttachment') && !device.app,
    },
    {
      type: ContentPickerSource.MediaLibrary,
      text: __('Photo and Video Library'),
      ariaLabel: __('Upload from photo library'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to upload from photo library'),
        __('A creator has disabled the option to upload from photo library'),
      ),
      icon: 'photo_video_outline',
      activeIcon: 'photo_video',
      color: 'blue',
      visible: device.app,
    },
    {
      type: ContentPickerSource.PhotoBooth,
      text: __('Camera'),
      ariaLabel: __('Take a photo'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to take a photo'),
        __('A creator has disabled the option to take a photo'),
      ),
      icon: 'camera_outline',
      activeIcon: 'camera',
      color: 'blue',
      visible: true,
    },
    {
      type: ContentPickerSource.VideoRecorder,
      text: __('Video recorder'),
      ariaLabel: __('Record a video'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to record a video'),
        __('A creator has disabled the option to record a video'),
      ),
      icon: 'video_outline',
      activeIcon: 'video',
      color: 'blue',
      visible: supportsVideoRecording || device.mobile,
    },
    {
      type: ContentPickerSource.AudioRecorder,
      text: __('Audio recorder'),
      ariaLabel: __('Record an audio'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to record an audio'),
        __('A creator has disabled the option to record an audio'),
      ),
      icon: 'microphone_outline',
      activeIcon: 'microphone',
      color: 'blue',
      visible: supportsRecording,
    },
    {
      type: ContentPickerSource.ScreenshareRecorder,
      text: __('Screen recorder'),
      ariaLabel: __('Record your screen'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to record your screen'),
        __('A creator has disabled the option to record your screen'),
      ),
      icon: 'screen_recorder_outline',
      activeIcon: 'screen_recorder',
      color: 'blue',
      visible: supportsScreenCapture,
    },
    {
      type: ContentPickerSource.DrawingCanvas,
      text: __('Draw'),
      ariaLabel: __('Draw something'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to draw'),
        __('A creator has disabled the option to draw'),
      ),
      icon: 'draw_outline',
      activeIcon: 'draw',
      color: 'blue',
      visible: context === 'post',
    },
    {
      type: ContentPickerSource.Poll,
      text: __('Poll'),
      ariaLabel: __('Make a poll'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to make a poll'),
        __('A creator has disabled the option to make a poll'),
      ),
      icon: 'poll_outline',
      activeIcon: 'poll',
      color: 'green',
      visible: true,
    },
    {
      type: ContentPickerSource.GoogleDrive,
      text: __('Google Drive'),
      ariaLabel: __('Upload from Google Drive'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to upload from Google Drive'),
        __('A creator has disabled the option to upload from Google Drive'),
      ),
      icon: 'google_drive_outline',
      activeIcon: 'google_drive_fill',
      color: 'green',
      visible: !device.app,
    },
    {
      type: ContentPickerSource.MicrosoftOneDrive,
      text: __('OneDrive'),
      ariaLabel: __('Upload from Microsoft OneDrive'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to upload from Microsoft OneDrive'),
        __('A creator has disabled the option to upload from Microsoft OneDrive'),
      ),
      icon: 'microsoft_onedrive_outline',
      activeIcon: 'microsoft_onedrive_fill',
      color: 'green',
      visible: isAppUsing('microsoftOneDrivePicker') && !device.app,
    },
    {
      type: ContentPickerSource.AiImageSearch,
      text: __('AI image'),
      ariaLabel: __('Use AI Art Generator'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to use AI Art Generator'),
        __('A creator has disabled the option to use AI Art Generator'),
      ),
      icon: 'ai_images',
      activeIcon: 'ai_images_filled',
      color: 'green',
      visible: isAppUsing('aiImageSearch'),
    },
    {
      type: ContentPickerSource.AiTalk,
      text: __('AI speech'),
      ariaLabel: __('Use AI Speech Generator'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to use AI Speech Generator'),
        __('A creator has disabled the option to use AI Speech Generator'),
      ),
      icon: 'communicate',
      activeIcon: 'communicate_filled',
      color: 'green',
      visible: !device.app,
    },
    {
      type: ContentPickerSource.PhotoAlbum,
      text: __('Photo album'),
      ariaLabel: __('Create a photo album containing up to %{count} images', { count: MAX_PHOTO_ALBUM_ATTACHMENTS }),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to pick multiple photos'),
        __('A creator has disabled the option to pick multiple photos'),
      ),
      icon: 'photo_video_outline',
      activeIcon: 'photo_video',
      color: 'green',
      visible: context === 'post',
    },
    {
      type: ContentPickerSource.DeviceUploads,
      text: __('From mobile'),
      ariaLabel: __('Upload from mobile device'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to upload from mobile'),
        __('A creator has disabled the option to upload from mobile'),
      ),
      icon: 'upload_from_mobile',
      activeIcon: 'upload_from_mobile',
      color: 'green',
      visible: isAppUsing('deviceUploads') && context !== 'whiteboard' && !device.app && !device.mobile,
    },
    {
      type: ContentPickerSource.ImageSearch,
      text: __('Image search'),
      ariaLabel: __('Search for an image'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to search for an image'),
        __('A creator has disabled the option to search for an image'),
      ),
      icon: 'image_search_outline',
      activeIcon: 'image_search',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.Stickers,
      text: __('Stickers'),
      ariaLabel: __('Add a sticker'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to add a sticker'),
        __('A creator has disabled the option to add a sticker'),
      ),
      icon: 'stickers',
      activeIcon: 'stickers_filled',
      color: 'orange',
      visible: context === 'whiteboard',
    },
    {
      type: ContentPickerSource.GifSearch,
      text: __('GIF'),
      ariaLabel: __('Search for a GIF'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to search for a GIF'),
        __('A creator has disabled the option to search for a GIF'),
      ),
      icon: 'gifs',
      activeIcon: 'gif',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.VideoSearch,
      text: __('YouTube'),
      ariaLabel: __('Search on YouTube'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to search on YouTube'),
        __('A creator has disabled the option to search on YouTube'),
      ),
      icon: 'youtube_outline',
      activeIcon: 'youtube',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.AudioSearch,
      text: __('Spotify'),
      ariaLabel: __('Search on Spotify'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to search on Spotify'),
        __('A creator has disabled the option to search on Spotify'),
      ),
      icon: 'spotify_outline',
      activeIcon: 'spotify',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.WebBrowser,
      text: __('Web search'),
      ariaLabel: __('Search on the web'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to search on the web'),
        __('A creator has disabled the option to search on the web'),
      ),
      icon: 'web_search_outline',
      activeIcon: 'web_search',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.MapBrowser,
      text: __('Location'),
      ariaLabel: __('Add a location'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to add a location'),
        __('A creator has disabled the option to add a location'),
      ),
      icon: 'location_outline',
      activeIcon: 'location',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.ThreeDObjectSearch,
      text: p__('3D is short for three dimensional', '3D model'),
      ariaLabel: __('Search for a 3D object'),
      disabledAriaLabel: wallAdminRoleLabel(
        __('An admin has disabled the option to search for a 3D object'),
        __('A creator has disabled the option to search for a 3D object'),
      ),
      icon: 'cube',
      activeIcon: 'cube',
      color: 'orange',
      visible: context === 'post' && isAppUsing('threeDAttachment'),
    },
  ].filter((source) => source.visible) as ContentSource[]

const getContentSourcesByColor = (): Record<string, ContentSource[]> =>
  groupBy(getContentSources('post'), (source) => source.color)

const getWhiteboardContentSources = (): ContentSource[] => {
  return getContentSources('whiteboard')
}

const SEARCH_MODE_BY_CONTENT_SOURCE = {
  [ContentPickerSource.ImageSearch]: SearchFilters.Images,
  [ContentPickerSource.GifSearch]: SearchFilters.Gif,
  [ContentPickerSource.VideoSearch]: SearchFilters.Videos,
  [ContentPickerSource.AudioSearch]: SearchFilters.Audio,
  [ContentPickerSource.WebBrowser]: SearchFilters.Web,
  [ContentPickerSource.ThreeDObjectSearch]: SearchFilters.ThreeDObjects,
} as const

const CONTENT_SOURCE_BY_SEARCH_MODE = {
  [SearchFilters.Images]: ContentPickerSource.ImageSearch,
  [SearchFilters.Gif]: ContentPickerSource.GifSearch,
  [SearchFilters.Videos]: ContentPickerSource.VideoSearch,
  [SearchFilters.Audio]: ContentPickerSource.AudioSearch,
  [SearchFilters.Web]: ContentPickerSource.WebBrowser,
  [SearchFilters.AiImages]: ContentPickerSource.AiImageSearch,
  [SearchFilters.Stickers]: ContentPickerSource.Stickers,
  [SearchFilters.ThreeDObjects]: ContentPickerSource.ThreeDObjectSearch,
} as const

const MEDIA_SEARCH_CONTENT_SOURCES = [
  ContentPickerSource.ImageSearch,
  ContentPickerSource.GifSearch,
  ContentPickerSource.VideoSearch,
] as const

const MEDIA_SEARCH_MODES = MEDIA_SEARCH_CONTENT_SOURCES.map((source) => SEARCH_MODE_BY_CONTENT_SOURCE[source])

const ALL_AV_SOURCES: ContentPickerSource[] = [
  ContentPickerSource.PhotoBooth,
  ContentPickerSource.VideoRecorder,
  ContentPickerSource.AudioRecorder,
  ContentPickerSource.ScreenshareRecorder,
]

const DEVICE_UPLOADABLE_CONTENT_SOURCES: ContentPickerSource[] = [
  ContentPickerSource.FileBrowser,
  ContentPickerSource.PhotoBooth,
  ContentPickerSource.VideoRecorder,
  ContentPickerSource.AudioRecorder,
  ContentPickerSource.DrawingCanvas,
  ContentPickerSource.PhotoAlbum,
]

const getAiSearchSamplePrompt = (): string =>
  'An expressive oil painting of a basketball player dunking, as depicted as an explosion of a nebula.'
const getTranslatedAiSearchSamplePrompt = (): string =>
  __('An expressive oil painting of a basketball player dunking, as depicted as an explosion of a nebula')

/**
 * Extract all visible ContentPickerSource values from a grouped menu list.
 * Only returns items whose `type` matches a valid ContentPickerSource enum value.
 */
function extractVisibleSources(sources: ContentPickerSourceMenuListItem[][]): ContentPickerSource[] {
  const visibleTypes = new Set<string>()
  for (const group of sources) {
    for (const item of group) {
      if (item.visible !== false) {
        visibleTypes.add(item.type)
      }
    }
  }
  const allValues = Object.values(ContentPickerSource) as string[]
  return allValues.filter((s) => visibleTypes.has(s)) as ContentPickerSource[]
}

export {
  ALL_AV_SOURCES,
  ALLOWED_IMAGE_PICKER_SOURCES,
  areAllDeviceUploadableSourcesDisabledFromAttachmentTypeStates,
  areAllImagePickerSourcesDisabledFromAttachmentTypeStates,
  areAllImagePickerSourcesDisabledFromSettings,
  getContentPickerSourceAriaLabelsForSettings,
  CONTENT_SOURCE_BY_SEARCH_MODE,
  ContentPickerSource,
  DEVICE_UPLOADABLE_CONTENT_SOURCES,
  extractVisibleSources,
  getAiSearchSamplePrompt,
  getContentSources,
  getContentSourcesByColor,
  getTranslatedAiSearchSamplePrompt,
  getWhiteboardContentSources,
  isAVScreenRecorderSupported,
  LOADING_IMAGES_BG_COLOR,
  MEDIA_SEARCH_CONTENT_SOURCES,
  MEDIA_SEARCH_MODES,
  SEARCH_MODE_BY_CONTENT_SOURCE,
}
export type { ContentPickerSourceMenuListItem, ContentSource, NoSearchFilterError }
